import { type FC, useCallback } from 'react';
import { Radio, Switch, Tooltip } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { useTpoGenConfig } from '../context/CustomerConfigContext';
import { usePlanDetailsContext } from './context/PlanDetailsContext';
import {
    TPO_PLAN_VIEW_HEADER,
    TPO_PLAN_VIEW_OPERATIONAL_PLAN_ICON,
    TPO_PLAN_VIEW_OPTIMIZATION_STATUS,
    TPO_PLAN_VIEW_OPTIMIZED_SWITCH,
    TPO_PLAN_VIEW_PLAN_NAME,
    TPO_PLAN_VIEW_TAB_CALENDAR,
    TPO_PLAN_VIEW_TAB_METRIC,
} from '../config/testIds';
import { getOptimizationStatusIcon } from '../utils/optimizationStatus';
import EditSessionControls from './EditSession/components/EditSessionControls';
import StarIcon from 'assets/svg/Star';
import { TPOGEN_OPTIMIZED_VERSION_VIEWED, TPOGEN_PLAN_VIEW_CHANGED } from 'utils/mixpanelEventNames';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../utils/trackTpoGenEvent';

interface PlanViewHeaderProps {
    planId: string;
    planName: string;
}

const PlanViewHeader: FC<PlanViewHeaderProps> = ({ planId, planName }) => {
    const tpoGenConfig = useTpoGenConfig();
    const {
        activeView,
        setActiveView,
        currentPlanDetails,
        optimizedPlanId,
        showOptimizedView,
        setShowOptimizedView,
        optimizationPollingStatus,
    } = usePlanDetailsContext();

    const radioValue = activeView === 'metric-view' ? 'metric-view' : 'calendar';
    const optimizationCompleted = !!optimizedPlanId;
    // A successful run is only meaningful while its calendar is on screen. Every other
    // state (running, infeasible, errored) produces no optimized plan to toggle to, so
    // it stays visible regardless of which calendar is selected.
    const optimizationRunStatus = optimizationPollingStatus?.status;
    const isSuccessfulRun =
        optimizationRunStatus === 'completed' && optimizationPollingStatus?.outcome !== 'infeasible';
    const showStatusIcon = isSuccessfulRun ? showOptimizedView : Boolean(optimizationRunStatus);

    const handleChange = useCallback(
        (e: RadioChangeEvent) => {
            const next = e.target.value as 'calendar' | 'metric-view';
            trackTpoGenEvent(TPOGEN_PLAN_VIEW_CHANGED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_CALENDAR, {
                plan_id: planId,
                view: next === 'metric-view' ? 'metric' : 'calendar',
            });
            setActiveView(next);
        },
        [setActiveView, planId],
    );

    const handleOptimizedToggleChange = useCallback(() => {
        const next = !showOptimizedView;
        trackTpoGenEvent(TPOGEN_OPTIMIZED_VERSION_VIEWED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_CALENDAR, {
            toggle: next ? 'optimized' : 'original',
            plan_id: planId,
        });
        setShowOptimizedView(next);
    }, [showOptimizedView, setShowOptimizedView, planId]);

    return (
        <div className="flex flex-row justify-between items-center" data-testid={TPO_PLAN_VIEW_HEADER}>
            <div className="flex flex-row items-center gap-x-3">
                {showStatusIcon ? (
                    <span data-testid={TPO_PLAN_VIEW_OPTIMIZATION_STATUS} data-state-status={optimizationRunStatus}>
                        {getOptimizationStatusIcon(optimizationPollingStatus)}
                    </span>
                ) : null}
                {currentPlanDetails?.is_operational_plan ? (
                    <Tooltip title={tpoGenConfig.labels.planDetail.operationalPlanTooltip}>
                        <span data-testid={TPO_PLAN_VIEW_OPERATIONAL_PLAN_ICON}>
                            <StarIcon />
                        </span>
                    </Tooltip>
                ) : null}
                <p className="text-xl font-medium truncate max-w-[200px]" data-testid={TPO_PLAN_VIEW_PLAN_NAME}>
                    {planName || planId}
                </p>
                <Radio.Group value={radioValue} onChange={handleChange}>
                    <Radio.Button
                        value="calendar"
                        data-testid={TPO_PLAN_VIEW_TAB_CALENDAR}
                        data-state-active={radioValue === 'calendar' ? 'true' : undefined}
                    >
                        {'Calendar View'}
                    </Radio.Button>
                    <Radio.Button
                        value="metric-view"
                        data-testid={TPO_PLAN_VIEW_TAB_METRIC}
                        data-state-active={radioValue === 'metric-view' ? 'true' : undefined}
                    >
                        {'Metric View'}
                    </Radio.Button>
                </Radio.Group>
            </div>
            <div className="flex flex-row items-center gap-x-3">
                {optimizationCompleted && (
                    <div className="flex items-center gap-1">
                        <span className="text-xs">Original</span>
                        <Switch
                            data-testid={TPO_PLAN_VIEW_OPTIMIZED_SWITCH}
                            data-state-active={showOptimizedView ? 'true' : undefined}
                            checked={showOptimizedView}
                            onChange={handleOptimizedToggleChange}
                        />
                        <span className="text-xs">Optimized</span>
                    </div>
                )}
                <EditSessionControls />
            </div>
        </div>
    );
};

PlanViewHeader.displayName = 'PlanViewHeader';
export default PlanViewHeader;
